import type {RoadmapModule} from 'course/editors/roadmapEditor/roadmapSchema';

/**
 * The modules that share the earliest release among `modules`: the next
 * week. `startDate` is a fixed-format Pacific wall-clock string, so string
 * order is time order.
 *
 * @param modules - Unreleased modules, by id.
 * @return The earliest-released ones, by id; empty when there are none.
 * @throws Never.
 */
export function selectNextReleaseModules(
  modules: Record<string, RoadmapModule>,
): Record<string, RoadmapModule> {
  const starts = Object.values(modules).map(module => module.startDate);
  if (starts.length === 0) {
    return {};
  }
  const nextStart = starts.reduce((earliest, start) =>
    start < earliest ? start : earliest,
  );
  return Object.fromEntries(
    Object.entries(modules).filter(([, module]) => module.startDate === nextStart),
  );
}

/**
 * The unreleased modules a student sees on the roadmap, locked: every one
 * set to `shown`, and the next release's modules unless set to `hidden`.
 * The next release is found among all of `modules`, so hiding next week
 * does not bring the week after it forward.
 *
 * @param modules - A roadmap's unreleased modules, by id.
 * @return The ones students see before release, by id.
 * @throws Never.
 */
export function selectUpcomingShownModules(
  modules: Record<string, RoadmapModule>,
): Record<string, RoadmapModule> {
  const next = selectNextReleaseModules(modules);
  return Object.fromEntries(
    Object.entries(modules).filter(
      ([id, module]) =>
        module.roadmapVisibility === 'shown' ||
        (module.roadmapVisibility !== 'hidden' && id in next),
    ),
  );
}
